import { AdCreative, PacketStrategy, PacketStrategyGroup } from '@marketing-milk/interfaces'

export interface StrategyGroupView {
  group: PacketStrategyGroup
  creatives: AdCreative[]
  /** creativeID -> pinned, for the ads actually rendered in this group. */
  pinned: Set<number>
}

export interface StrategyView {
  groups: StrategyGroupView[]
  /**
   * Ads in no group. This is DERIVED here rather than sent by the server: the
   * frontend has both inputs, and deriving it is the safer failure mode — a
   * creative missing from every group shows up rather than vanishing.
   *
   * It means "arrived since the last generation", which is also why zero
   * strategy data collapses to a single Ungrouped bucket containing everything
   * — today's flat grid, for free.
   */
  ungrouped: AdCreative[]
}

export function joinStrategy(
  strategy: PacketStrategy | undefined,
  creatives: AdCreative[] | undefined
): StrategyView {
  const all = creatives ?? []
  if (!strategy || strategy.groups.length === 0) {
    return { groups: [], ungrouped: all }
  }

  const byID = new Map(all.map(c => [c.id, c]))
  const placed = new Set<number>()

  const groups = strategy.groups.map(group => {
    const groupCreatives: AdCreative[] = []
    const pinned = new Set<number>()
    for (const ad of group.ads) {
      const creative = byID.get(ad.creativeID)
      // A membership row can outlive its creative's visibility (hidden, soft
      // deleted). Skip it rather than rendering a hole.
      if (!creative) continue
      groupCreatives.push(creative)
      placed.add(creative.id)
      if (ad.pinned) pinned.add(creative.id)
    }
    return { group, creatives: groupCreatives, pinned }
  })

  return { groups, ungrouped: all.filter(c => !placed.has(c.id)) }
}
